/** How a dropped item falls and lies on the ground: `CPythonItem::CreateItem`'s
 *  resting turn (`UserInterface/PythonItem.cpp:298-445`) and
 *  `TGroundItemInstance::Update`'s 300 ms fall onto it (`:38-99`). */

export type Vec3 = readonly [number, number, number];
/** A unit quaternion as D3DX lays one out: x, y, z, w. */
export type Quat = readonly [number, number, number, number];

/** `dwEndTime = dwStartTime + 300` (`PythonItem.cpp:444`). */
export const DROP_FALL_MS = 300;

/** `GetHeight(x, y) + 10.0f`, ten client units above the ground (`PythonItem.cpp:306`). */
export const DROP_LIFT_MM = 10 * 10;

/** One client unit in millimetres. */
const UNIT_MM = 10;

/** `D3DXQuaternionMultiply(out, a, b)`: `a` applied first, then `b`. */
function multiply(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = b;
  const [bx, by, bz, bw] = a;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

/** `v` turned by `q`: the `qc * (v, 0) * q` product `Update` builds (`PythonItem.cpp:54-58,82-86`). */
export function rotate(q: Quat, v: Vec3): Vec3 {
  const [x, y, z, w] = q;
  const cx = y * v[2] - z * v[1];
  const cy = z * v[0] - x * v[2];
  const cz = x * v[1] - y * v[0];
  return [
    v[0] + 2 * (w * cx + y * cz - z * cy),
    v[1] + 2 * (w * cy + z * cx - x * cz),
    v[2] + 2 * (w * cz + x * cy - y * cx),
  ];
}

function normalize(v: Vec3): Vec3 {
  const length = Math.hypot(v[0], v[1], v[2]);
  return [v[0] / length, v[1] / length, v[2] / length];
}

/** `RotationArc` (`EterLib/GrpMath.h:62-78`): the turn carrying `from` onto `to`. */
function rotationArc(from: Vec3, to: Vec3): Quat {
  const a = normalize(from);
  const b = normalize(to);
  const d = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const s = Math.sqrt((1 + d) * 2);
  return [(a[1] * b[2] - a[2] * b[1]) / s, (a[2] * b[0] - a[0] * b[2]) / s, (a[0] * b[1] - a[1] * b[0]) / s, s * 0.5];
}

/** `D3DXQuaternionRotationAxis`. */
function rotationAxis(axis: Vec3, radians: number): Quat {
  const [x, y, z] = normalize(axis);
  const half = Math.sin(radians / 2);
  return [x * half, y * half, z * half, Math.cos(radians / 2)];
}

/** A seeded stand-in for `random()` and `frandom`: the original draws from a
 *  generator no client shares, so one drop's turn is a function of its id. */
function draws(seed: number): () => number {
  let state = (seed >>> 0) || 1;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

interface Turn {
  readonly end: Quat;
  readonly axis: Vec3;
}

function turnFor(min: Vec3, max: Vec3, seed: number, normal: Vec3): Turn {
  const next = draws(seed);
  const frandom = (lo: number, hi: number): number => lo + next() * (hi - lo);
  // `std::sort` of the three extents with their axis (`PythonItem.cpp:327-335`).
  const flattest = [0, 1, 2]
    .map((axis) => [max[axis] - min[axis], axis] as const)
    .sort((a, b) => a[0] - b[0] || a[1] - b[1])[0][1];
  // `bStabGround = false` after the weapon test (`:315`): every drop takes the
  // flat branch, its thinnest axis turned onto +z (`:386-417`).
  const lying: number[] = [0, 0, 0];
  for (let axis = 0; axis < 3; axis += 1) {
    lying[axis] = axis === flattest ? Math.floor(next() * 2) * 2 - 1 + frandom(-0.1, 0.1) : frandom(-0.1, 0.1);
  }
  let end = rotationArc(lying as unknown as Vec3, [0, 0, 1]);
  const rot = frandom(0, 2 * 3.1415926535); // PythonItem.cpp:420
  end = multiply(end, [0, 0, Math.cos(rot), Math.sin(rot)]); // PythonItem.cpp:421-422
  end = multiply(end, rotationArc([0, 0, 1], normal)); // PythonItem.cpp:423-424
  return { end, axis: [Math.sin(rot), Math.cos(rot), 0] }; // PythonItem.cpp:446-448
}

/** `qEnd`, the turn a drop comes to rest in on ground whose normal is `normal`. */
export function restingTurn(min: Vec3, max: Vec3, seed: number, normal: Vec3 = [0, 0, 1]): Quat {
  return turnFor(min, max, seed, normal).end;
}

export interface DropPose {
  readonly rotation: Quat;
  readonly positionMm: Vec3;
  readonly landed: boolean;
}

/** Where the model's origin sits and how it is turned `elapsedMs` after it was
 *  dropped at `groundMm`, its model-space box `min`..`max` in millimetres. */
export function dropPose(
  min: Vec3,
  max: Vec3,
  seed: number,
  groundMm: Vec3,
  elapsedMs: number,
  normal: Vec3 = [0, 0, 1],
): DropPose {
  const { end, axis } = turnFor(min, max, seed, normal);
  const centre: Vec3 = [-(min[0] + max[0]) / 2, -(min[1] + max[1]) / 2, -(min[2] + max[2]) / 2];
  const rest: Vec3 = [groundMm[0], groundMm[1], groundMm[2] + DROP_LIFT_MM];
  const landed = DROP_FALL_MS < elapsedMs; // PythonItem.cpp:42
  const rate = landed ? 1 : Math.max(0, elapsedMs) / DROP_FALL_MS; // PythonItem.cpp:72
  const rotation = landed
    ? end
    : multiply(end, rotationAxis(axis, (DROP_FALL_MS - Math.max(0, elapsedMs)) * 0.03 * (-1 + rate * (3 * rate - 2)))); // PythonItem.cpp:77-79
  const lift = landed ? 0 : (100 - 100 * rate * (3 * rate - 2)) * UNIT_MM; // PythonItem.cpp:75
  const adjust = rotate(rotation, centre);
  return {
    rotation,
    positionMm: [rest[0] + adjust[0], rest[1] + adjust[1], rest[2] + lift + adjust[2]],
    landed,
  };
}
